import {
  Stack,
  useTheme,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  SelectChangeEvent,
} from "@mui/material";
import PageChangeWarningDialog from "../common/PageChangeWarningDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Outlet, useOutletContext } from "react-router-dom";
// EndUserView.tsx (add these imports at the top)
import { useNavigate } from "react-router-dom";
import { Divider } from "@mui/material";
import { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";

import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import MinimizeIcon from "@mui/icons-material/Minimize";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ReplayIcon from "@mui/icons-material/Replay";
import { useArrayActions, useArrayData } from "../../context/ArrayDataContext";

import WarningIcon from "@mui/icons-material/Warning";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedDropdownValue,
  setSelectedRow,
} from "../../redux/actions/supportTicket";
import Download from "../common/DownloadGrid/Download";
import FormController from "../common/FormController/FormController";
import ButtonComponent from "../common/button/Button";
import BulkPasteFilter from "../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../common/FilterVariant/FilterManagerButton";

export type EndUserOutletContext = {
  gridRef: any;
  gridTheme: any;
  rowsPerPage: number;
  setGridReady: (b: boolean) => void;
  sideBar: any;
  loading: boolean;
  columns: any[];
  outputColumns: any[];
  inputRows: any[];
  approvedRows: any[];
  rejectedRows: any[];
  approvedBothRows: any[];
  dataErrorRows: any[];

  selectedItemsdata: any[];
  setSelectedHistoryRow: (row: any) => void;
  setOpenHistory: (open: boolean) => void;

  handleGridChange: (data?: any) => void;
  onRowSelected: (row?: any) => void;

  handleHeaderTakeActionClick: () => void;
  handleSubmitClick: () => void;
  currency: "USD" | "LOCAL";
  setCurrency: (value: "USD" | "LOCAL") => void;
};

export function useEndUserOutletContext() {
  return useOutletContext<EndUserOutletContext>();
}

type RenderTableProps = {
  gridRef?: any;
  gridTheme?: any;
  setGridReady?: (b: boolean) => void;
  columns?: any[];
  rows?: any[];
  rowsPerPage?: number;
  handleChangePage?: (e?: any) => void;
  sideBar?: any;
  label?: string | null;
  resizable?: boolean;
  minimized?: string | null;
  emitEvent?: (action?: string) => void;
  onChange?: (data?: any) => void;
  onRowSelected?: (row?: any) => void;
  selectedItemsData?: any[];
  setSelectedHistoryRow?: (row: any) => void;
  setOpenHistory?: (open: boolean) => void;
  loading?: boolean;
  currency: "USD" | "LOCAL";
  onCurrencyChange: (value: "USD" | "LOCAL") => void;
  onSelectionChanged?: (event: any) => void;
  hideHeaderActions?: boolean;
  onResetFilters?: () => void;
  selectedCount?: number;
  isTriggerFilter?: boolean;
  setIsTriggerFilter?: React.Dispatch<React.SetStateAction<boolean>>;
};

const RenderTable: React.FC<RenderTableProps> = ({
  gridRef,
  gridTheme,
  setGridReady,
  columns,
  rows,
  rowsPerPage,
  handleChangePage,
  sideBar,
  label,
  resizable,
  minimized,
  emitEvent = () => {},
  onChange,
  onRowSelected,
  selectedItemsData,
  setSelectedHistoryRow,
  setOpenHistory,
  loading = false,
  onCurrencyChange,
  currency,
  onSelectionChanged,
  hideHeaderActions = false,
  onResetFilters,
  isTriggerFilter,
  selectedCount = 0,
  setIsTriggerFilter
}) => {
  const theme = useTheme();
  const selectedItem =
    selectedItemsData?.length > 0 ? selectedItemsData[0] : [];

  const handleCurrencyChange = (event: SelectChangeEvent) => {
    onCurrencyChange(event.target.value as "USD" | "LOCAL");
  };
 
  useEffect(() => {
    if (gridRef?.current?.api) {
      // Re-evaluate all active filters against the new currency-converted row values.
      // refreshCells alone only re-renders cell UI; onFilterChanged forces AG Grid
      // to re-run the filter model so LOCAL-mode values are correctly matched.
      gridRef.current.api.onFilterChanged();
      gridRef.current.api.refreshCells({ force: true });
    }
  }, [currency]);

  // Always include a pinned selection column with checkboxes
  const finalColumns = useMemo(() => {
    const selectCol = {
      field: "__select__",
      headerName: "",
      width: 40,
      pinned: "left",
      checkboxSelection: true, // per-row checkbox
      headerCheckboxSelection: true, // select-all in header
      headerCheckboxSelectionCurrentPageOnly: true, // only selects current page
      headerCheckboxSelectionFilteredOnly: true, // only selects filtered rows
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      resizable: false,
      // ensure clicks on the checkbox are not blocked by parent styles
      cellStyle: { textAlign: "center", pointerEvents: "auto" },
    };
    return [selectCol, ...(columns ?? [])];
  }, [columns]);

  // Memoized so AG Grid always receives the same object reference.
  // Passing a new inline object on every render causes AG Grid to detect that
  // filterValueGetter changed and clear the active filter model, making all
  // rows reappear after checkbox clicks.
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "18px",
        textAlign: "center",
      },
      valueFormatter: (p: any) => {
        const isSelectionCol =
          p.colDef.checkboxSelection ||
          p.colDef.headerCheckboxSelection ||
          !p.colDef.field ||
          p.column.getColId() === "ag-Grid-AutoColumn";
        if (isSelectionCol) return "";
        const v = p.value;
        if (
          v === null ||
          v === undefined ||
          (typeof v === "string" && v.trim() === "") ||
          (typeof v === "number" && Number.isNaN(v))
        ) {
          return "-";
        }
        return v;
      },
      filterValueGetter: (params: any) => {
        const v = params.data?.[params.colDef?.field];
        if (v === null || v === undefined || v === "-" || v === "") return "";
        const str = String(v);
        const noCommas = str.replace(/,/g, "");
        const asNum = Number(noCommas.replace(/[$€£¥%]/g, "").trim());
        return Number.isFinite(asNum) ? noCommas : str;
      },
      comparator: (a: any, b: any) => {
        const toNum = (v: any) => {
          if (v === null || v === undefined || v === "-" || v === "")
            return null;
          const n = Number(
            String(v)
              .replace(/,/g, "")
              .replace(/[$€£¥%]/g, "")
              .trim(),
          );
          return Number.isFinite(n) ? n : null;
        };
        const na = toNum(a);
        const nb = toNum(b);
        if (na !== null && nb !== null) return na - nb;
        return String(a ?? "").localeCompare(String(b ?? ""));
      },
    }),
    [],
  );
  const overLay = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} sx={{ color: "#0a0101" }} />
      </Box>
    );
  };

  const handleSelectionHeaderClick = useCallback((e: any) => {
    const colId = e?.column?.getColId?.();
    const isSelectionHeader =
      colId === "ag-Grid-SelectionColumn" || colId === "__select__";
    if (!isSelectionHeader) return;

    const api = e?.api;
    if (!api) return;

    // Let AG Grid finish header checkbox toggle first.
    setTimeout(() => {
      const currentPage = api.paginationGetCurrentPage?.() ?? 0;
      const pageSize = api.paginationGetPageSize?.() ?? 0;
      const displayedCount = api.getDisplayedRowCount?.() ?? 0;
      const start = currentPage * pageSize;
      const end = Math.min(start + pageSize, displayedCount);

      let selectedOnPage = 0;
      for (let i = start; i < end; i += 1) {
        const node = api.getDisplayedRowAtIndex?.(i);
        if (node?.isSelected?.()) selectedOnPage += 1;
      }

      const totalSelected = api.getSelectedNodes?.().length ?? 0;
      if (totalSelected > 0 && selectedOnPage === 0) {
        api.deselectAll?.();
      }
    }, 0);
  }, []);
  return (
    <ConditionalWrapper
      condition={minimized !== label}
      elseContent={
        <Box paddingLeft={1}>
          <Typography>
            {label} is minimized, Click here to restore
            <IconButton onClick={() => emitEvent("")}>
              <ArrowOutwardIcon color="primary" />
            </IconButton>
          </Typography>
        </Box>
      }
    >
      {/* MAIN CONTAINER – COLUMN */}
      <Stack
        direction="column"
        sx={{
          width: "100%",
          minHeight: resizable ? "180px" : null,
          height: resizable ? "250px" : null,
          resize: resizable ? "vertical" : null,
          overflow: resizable ? "auto" : null,
          maxHeight:
            resizable && minimized !== "" && minimized !== label
              ? null
              : resizable
                ? "325px"
                : null,
          flexGrow: resizable
            ? null
            : minimized !== "" && minimized !== label
              ? 4
              : 1,
          flexShrink: minimized === label ? 1 : 0,
        }}
      >
        {/* HEADER + DROPDOWN */}
        <Stack spacing={1} sx={{ px: 2, pb: 1 }}>
          {/* LABEL */}
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>

          {/* DROPDOWN – LEFT ALIGNED */}
          {!hideHeaderActions && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <FormController
                  currency={currency}
                  handleCurrencyChange={handleCurrencyChange}
                />
                { (rows?.length ?? 0) > 0 && (
                  <><ButtonComponent
                    className="take-action"
                    type="secondary"
                    label="Reset Filters"
                    onClick={onResetFilters}
                    width="auto"
                    maxWidth="none"
                    styles={{ borderRadius: 28 }}
                  /><FilterManagerButton    
                             gridRef={ gridRef }
                             tableName="Safety Stock Review List -Output view"    
                             userEmail={localStorage.getItem("userEmail") || ""}    
                             disabled={!(rows?.length ?? 0)}  />
                  <BulkPasteFilter
                  gridRef={gridRef}
                  columns={columns}
                  setIsTriggerFilter={setIsTriggerFilter}
                  triggerClearFilter={isTriggerFilter}
                /></>
                ) }
                <Download
                  loading={loading}
                  gridRef={gridRef}
                  fileName={label ? `${label} ` : "Grid Export"}
                  rowData={rows}
                />
              </Box>
            </Box>
          )}
        </Stack>

        {/* TABLE – FULL WIDTH */}
        <Box sx={{ flex: 1, position: "relative", width: "100%" }}>
          <AgGridReact
            loading={loading}
            data-testid="ag-grid"
            loadingOverlayComponent={overLay}
            overlayNoRowsTemplate={"<span>No Data Available</span>"}
            ref={gridRef}
            onGridReady={() => setGridReady?.(true)}
            theme={gridTheme}
            columnDefs={finalColumns}
            rowSelection="multiple"
            suppressRowClickSelection
            pagination
            paginationPageSize={rowsPerPage}
            rowModelType="clientSide"
            rowData={rows}
            getRowId={(params) =>
              String(
                params.data.__uid ??
                  params.data.materialNumber ??
                  params.data.rowIndex,
              )
            }
            onPaginationChanged={handleChangePage}
            onColumnHeaderClicked={handleSelectionHeaderClick}
            gridOptions={{ sideBar }}
            onRowSelected={onRowSelected}
            stopEditingWhenCellsLoseFocus
            onCellValueChanged={onChange}
            singleClickEdit
            suppressMenuHide
            onSelectionChanged={onSelectionChanged}
            enableCellTextSelection
            suppressRowTransform
            defaultColDef={defaultColDef}
          />
          {selectedCount > 0 && (
            <Typography
              sx={{
                position: "absolute",
                left: 12,
                bottom: 8,
                zIndex: 2,
                fontSize: 13,
                fontWeight: 500,
                color: "text.secondary",
                pointerEvents: "none",
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {selectedCount}
              </Box>{" "}
              item{selectedCount !== 1 ? "s" : ""} selected
            </Typography>
          )}
        </Box>
      </Stack>
    </ConditionalWrapper>
  );
};

export const EndUserApprovedView: React.FC = () => {
  const ctx = useEndUserOutletContext();
  const isSelectionRemove = useSelector(
    (state: any) => state?.supportTicket?.isSelectionRemove ?? false,
  );

  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const prevPageRef = useRef<number | null>(null);
  const dropdownValuesSetRef = useRef(false);
  const pendingPageRef = useRef<number | null>(null);
  const suppressPaginationRef = useRef(false);
  const freezeSelectionRef = useRef(false);
  const selectionSnapshotRef = useRef<Set<string>>(new Set());
  const [openPageChangeWarning, setOpenPageChangeWarning] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
    const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const dispatch = useDispatch();
  const DeSelectItems = () => {
    const api = ctx.gridRef?.current?.api;
    if (api) api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedCount(0);
    dropdownValuesSetRef.current = false;
  };

  useEffect(() => {
    if (openPageChangeWarning) return;
    if (!isSelectionRemove) return;
    DeSelectItems();
  }, [isSelectionRemove, ctx.gridRef, openPageChangeWarning]);

  const restoreSelectionSnapshot = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    const snapshot = selectionSnapshotRef.current;
    if (!snapshot.size) return;
    api.forEachNode((node: any) => {
      node.setSelected(snapshot.has(String(node.id)));
    });
    setSelectedCount(snapshot.size);
  }, [ctx.gridRef]);

  const filteredApprovedRows = useMemo(() => {
    // Ensure each row has a unique __uid for AG Grid
    return (ctx.approvedRows ?? []).map((row: any, idx: number) => ({
      ...row,
      __uid: row.__uid || row.materialNumber || idx,
    }));
  }, [ctx.approvedRows]);
  const handleSelectionChanged = useCallback(
    (event: any) => {
      if (freezeSelectionRef.current) return;
      const nodes = event.api.getSelectedNodes();
      selectedRowIdsRef.current = new Set(nodes.map((n: any) => String(n.id)));
      setSelectedCount(selectedRowIdsRef.current.size);
      dispatch(setSelectedRow(nodes.map((n: any) => n.data)));

      // Set default dropdown values only once when rows are first selected
      if (nodes.length > 0 && !dropdownValuesSetRef.current) {
        dropdownValuesSetRef.current = true;
        dispatch(
          setSelectedDropdownValue({
            menuId: "Output View",
            categoryId: "Data",
            functionId: "Data",
          }),
        );
      }

      // Reset the flag when selection is cleared
      if (nodes.length === 0) {
        dropdownValuesSetRef.current = false;
      }
    },
    [dispatch],
  );

  const handlePageChange = useCallback(
    (e?: any) => {
      if (suppressPaginationRef.current) return;
      const api = ctx.gridRef?.current?.api;
      if (!api) return;

      const current = api.paginationGetCurrentPage?.() ?? 0;
      const prev = prevPageRef.current;

      // Initialize on first event
      if (prev === null) {
        prevPageRef.current = current;
        return;
      }

      if (current !== prev) {
        if (selectedRowIdsRef.current.size > 0) {
          freezeSelectionRef.current = true;
          selectionSnapshotRef.current = new Set(selectedRowIdsRef.current);
          suppressPaginationRef.current = true;
          api.paginationGoToPage(prev);
          suppressPaginationRef.current = false;
          pendingPageRef.current = current;
          setOpenPageChangeWarning(true);
        } else {
          api.deselectAll();
          selectedRowIdsRef.current = new Set();
          setSelectedCount(0);
          dropdownValuesSetRef.current = false;
          prevPageRef.current = current;
        }
      }
      // If current === prev, do nothing (don’t clear)
    },
    [ctx.gridRef],
  );

  const handlePageChangeConfirm = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    freezeSelectionRef.current = false;
    selectionSnapshotRef.current = new Set();
    api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedCount(0);
    dropdownValuesSetRef.current = false;
    if (pendingPageRef.current !== null) {
      suppressPaginationRef.current = true;
      api.paginationGoToPage(pendingPageRef.current);
      suppressPaginationRef.current = false;
      prevPageRef.current = pendingPageRef.current;
      pendingPageRef.current = null;
    }
    setOpenPageChangeWarning(false);
  }, [ctx.gridRef]);

  const handlePageChangeCancel = useCallback(() => {
    pendingPageRef.current = null;
    freezeSelectionRef.current = false;
    restoreSelectionSnapshot();
    setOpenPageChangeWarning(false);
  }, [restoreSelectionSnapshot]);

  const handleResetFilters = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    DeSelectItems();
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    setIsTriggerFilter(true);
    api.onFilterChanged();
  }, [ctx.gridRef]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <RenderTable
        gridRef={ctx.gridRef}
        columns={ctx.outputColumns}
        gridTheme={ctx.gridTheme}
        rows={filteredApprovedRows}
        rowsPerPage={ctx.rowsPerPage}
        setGridReady={ctx.setGridReady}
        sideBar={ctx.sideBar}
        minimized={null}
        emitEvent={() => {}}
        onChange={ctx.handleGridChange}
        onRowSelected={ctx.onRowSelected}
        selectedItemsData={ctx.selectedItemsdata}
        setSelectedHistoryRow={ctx.setSelectedHistoryRow}
        setOpenHistory={ctx.setOpenHistory}
        loading={ctx.loading}
        currency={ctx.currency}
        onCurrencyChange={ctx.setCurrency}
        onSelectionChanged={handleSelectionChanged}
        handleChangePage={handlePageChange}
        onResetFilters={handleResetFilters}
        selectedCount={selectedCount}
        isTriggerFilter={isTriggerFilter}
        setIsTriggerFilter={setIsTriggerFilter}
      />
      </Box>

      <PageChangeWarningDialog
        open={openPageChangeWarning}
        selectedCount={selectedCount}
        onCancel={handlePageChangeCancel}
        onConfirm={handlePageChangeConfirm}
      />
    </>
  );
};
